<template>
  <div class="secondaryUsers">
    <RouterView />
    <div class="box">
      <div class="title-container is-flex">
        <h2 class="title is-3 has-text-primary">
          Mis usuarios
        </h2>
        <div class="buttons">
          <div class="field is-grouped">
            <p class="control">
              <RouterLink
                class="button is-primary"
                :to="{ name: 'SecondaryUserPush' }"
              >
                <BIcon
                  icon="plus"
                />
                <span>
                  Nuevo usuario
                </span>
              </RouterLink>
            </p>
          </div>
        </div>
      </div>

      <div class="table-container">
        <BTable
          :data="$store.state.users.list"
          :loading="$store.state.users.loading"
          :total="$store.state.users.count"
          :current-page="page"
          :columns="columns"
          :per-page="size"
          backend-pagination
          paginated
          @page-change="changePage"
        >
          <template slot-scope="props">
            <BTableColumn
              field="name"
              label="Nombre"
              internal
            >
              {{ props.row.name }}
            </BTableColumn>

            <BTableColumn
              field="email"
              label="Email"
              internal
            >
              {{ props.row.email }}
            </BTableColumn>

            <BTableColumn
              field="updatedAt"
              label="Fecha última actualización"
              internal
            >
              {{ props.row.updatedAt | toHumanDateTime }}
            </BTableColumn>

            <BTableColumn
              field="controls"
              label=""
              internal
            >
              <AppRadialMenu
                :size="36"
                :item-size="48"
                :radius="70"
                :angle-restriction="90"
                :rotate="-70"
              >
                <AppRadialMenuItem
                  class="tooltip is-tooltip-success has-background-success has-text-white"
                  data-tooltip="Reenviar Credenciales"
                >
                  <a
                    class="action-option"
                    :class="{ 'is-loading': sendEmailLoading }"
                    :disabled="sendEmailLoading"
                    @click="sendEmail(props.row.userId)"
                  >
                    <BIcon
                      icon="send"
                      class="action-option-icon"
                    />
                  </a>
                </AppRadialMenuItem>

                <AppRadialMenuItem
                  class="
                    tooltip is-tooltip-danger has-background-danger has-text-white is-tooltip-left"
                  data-tooltip="Eliminar"
                >
                  <RouterLink
                    class="action-option"
                    :to="{ name: 'SecondaryDelete', params: { secondaryId: props.row.userId } }"
                  >
                    <BIcon
                      icon="delete"
                      class="action-option-icon"
                    />
                  </RouterLink>
                </AppRadialMenuItem>

                <AppRadialMenuItem
                  class="tooltip is-tooltip-link has-background-link has-text-white is-tooltip-left"
                  data-tooltip="Editar"
                >
                  <RouterLink
                    class="action-option"
                    :to="{ name: 'SecondaryEdit', params: { secondaryId: props.row.userId } }"
                  >
                    <BIcon
                      icon="pencil"
                      class="action-option-icon"
                    />
                  </RouterLink>
                </AppRadialMenuItem>
              </AppRadialMenu>
            </BTableColumn>
          </template>

          <template slot="empty">
            <section class="section">
              <div class="content has-text-grey has-text-centered">
                <p>
                  <BIcon
                    icon="emoticon-sad"
                    size="is-large"
                  />
                </p>
                <p>No tienes ningún usuario</p>
              </div>
            </section>
          </template>
        </BTable>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';

export default {
  name: 'SecondaryUsers',

  components: {
    AppRadialMenu: () => import(/* webpackChunkName: "app-radial-menu" */ '@/components/AppRadialMenu.vue'),
    AppRadialMenuItem: () => import(/* webpackChunkName: "app-radial-menu-item" */ '@/components/AppRadialMenuItem.vue'),
  },

  data() {
    return {
      sendEmailLoading: false,
      columns: [
        { field: 'name', label: 'Nombre' },
        { field: 'email', label: 'Email' },
        { field: 'updatedAt', label: 'Fecha última actualización' },
        { field: 'controls', label: '' },
      ],
    };
  },

  computed: {
    page() {
      return parseInt(this.$route.query.page, 10);
    },
    size() {
      return parseInt(this.$route.query.size, 10);
    },
  },

  beforeRouteEnter({ name, query }, from, next) {
    if (name === 'SecondaryUsers') {
      if (query.page && query.size) {
        next();
      } else {
        const { page = 1, size = 20 } = query;
        next({ name, query: { page, size } });
      }
    } else {
      next();
    }
  },

  async beforeRouteUpdate({ name, query }, from, next) {
    if (name === 'SecondaryUsers') {
      if (query.page && query.size) {
        const { page = 1, size = 20 } = query;
        await this.$store.dispatch('users/getSecondary', { page, size });
        next();
      } else {
        const { page = 1, size = 20 } = query;
        await this.$store.dispatch('users/getSecondary', { page, size });
        next({ name, query: { page, size } });
      }
    } else {
      next();
    }
  },

  async beforeMount() {
    const { page = 1, size = 20 } = this.$route.query;

    await this.$store.dispatch('users/getSecondary', { page, size });
  },

  methods: {
    ...mapActions({
      sendSecondarySignInEmail: 'users/sendSecondarySignInEmail',
    }),
    changePage(event) {
      this.$router.push({
        name: 'SecondaryUsers',
        query: { page: event, size: 20 },
      });
    },
    async sendEmail(id) {
      this.sendEmailLoading = true;

      try {
        await this.sendSecondarySignInEmail(id);

        this.$store.dispatch('addTemporalNotification', {
          type: 'success',
          content: 'Se envió el correo correctamente.',
        });
      } catch (error) {
        this.$store.dispatch('addTemporalNotification', {
          type: 'danger',
          content: 'Ocurrió un error al enviar el correo.',
        });
      }

      this.sendEmailLoading = false;
    },
  },
};
</script>

<style scoped>
.secondaryUsers {
  padding: 1rem;
  padding-bottom: 6rem;
}

.title-container.is-flex {
  justify-content: space-between;
  align-items: center;
  padding-bottom: 1rem;
  flex-wrap: wrap;
}

.title {
  margin-bottom: 0;
}

@media screen and (max-width: 768px) {
  .title {
    margin-bottom: 1rem;
  }
}
</style>

<style lang="scss">
.secondaryUsers .pagination {
  margin-right: 4px;
}
</style>
